import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { DesktopShell } from '../components/DesktopShell';
import { TopBar } from '../components/TopBar';
import { Card } from '../components/Card';
import { Btn } from '../components/Btn';
import { Pill } from '../components/Pill';
import { Avatar } from '../components/Avatar';
import { Ic } from '../components/Icon';
import { Modal } from '../components/Modal';
import { Seg } from '../components/Seg';
import { roomsApi } from '../api/rooms';
import { displayNick } from '../lib/nick';
import { useAuth } from '../store/auth';
import type { HttpError } from '../api/client';
import type { RoomInviteDto, UserSummary } from '../api/types';

type InviteAction =
  | { kind: 'confirm'; user: UserSummary }
  | { kind: 'already-member'; user: UserSummary }
  | { kind: 'already-invited'; user: UserSummary };

type LeaderboardTab = 'total' | 'group' | 'knockout';

const LEADERBOARD_TABS: { value: LeaderboardTab; label: string; icon: ReactNode }[] = [
  { value: 'total', label: 'Turniej', icon: <Ic.globe size={14} /> },
  { value: 'group', label: 'Faza grupowa', icon: <Ic.grid size={14} /> },
  { value: 'knockout', label: 'Faza pucharowa', icon: <Ic.trophy size={14} /> },
];

// Minimum border-box width of the tabs row for the full-label Seg to fit: natural
// Seg width with full Polish labels (measured ~372px) + the row's own 24px×2
// horizontal padding + a safety margin for font-rendering variance across
// browsers/OS. Below this the labels would get clipped, so we drop to icon-only.
const LEADERBOARD_TABS_MIN_ROW_WIDTH = 450;

export function RoomPage() {
  const { id } = useParams<{ id: string }>();
  const user = useAuth((s) => s.user);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [inviteEmail, setInviteEmail] = useState('');
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [inviteAction, setInviteAction] = useState<InviteAction | null>(null);
  const [inviteToCancel, setInviteToCancel] = useState<RoomInviteDto | null>(null);

  const room = useQuery({ queryKey: ['room', id], queryFn: () => roomsApi.detail(id!), enabled: !!id });
  const members = useQuery({ queryKey: ['room', id, 'members'], queryFn: () => roomsApi.members(id!), enabled: !!id });
  const pendingInvites = useQuery({
    queryKey: ['room', id, 'invites'],
    queryFn: () => roomsApi.listInvites(id!),
    enabled: !!id,
  });
  const leaderboard = useQuery({
    queryKey: ['room', id, 'leaderboard'],
    queryFn: () => roomsApi.leaderboard(id!),
    enabled: !!id,
  });
  const [lbTab, setLbTab] = useState<LeaderboardTab>('total');
  // Plain object ref + effect-on-mount doesn't work here: this row is behind the
  // `room.isLoading` early return below, so it isn't in the DOM yet on first mount.
  // A callback ref re-fires this effect once the node actually appears.
  const [tabsRowEl, setTabsRowEl] = useState<HTMLDivElement | null>(null);
  const [tabsCompact, setTabsCompact] = useState(false);
  useEffect(() => {
    if (!tabsRowEl) return;
    // Measure synchronously on attach (getBoundingClientRect forces a layout flush,
    // so this reflects the real final width) instead of only trusting the first
    // ResizeObserver callback, whose timing/initial value isn't reliable enough here.
    const measure = () => setTabsCompact(tabsRowEl.getBoundingClientRect().width < LEADERBOARD_TABS_MIN_ROW_WIDTH);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(tabsRowEl);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [tabsRowEl]);
  // Compact mode keeps every icon but only labels the active tab — that keeps the
  // row's width roughly constant (one label at a time) instead of needing to fit
  // all three, so it's the fallback for narrow layouts instead of hiding all labels.
  const leaderboardTabOptions = useMemo(() => {
    if (!tabsCompact) return LEADERBOARD_TABS;
    return LEADERBOARD_TABS.map((t) => (t.value === lbTab ? t : { ...t, label: '' }));
  }, [tabsCompact, lbTab]);
  const rankedLeaderboard = useMemo(() => {
    if (!leaderboard.data) return [];
    return [...leaderboard.data]
      .sort((a, b) =>
        b[lbTab].totalPoints - a[lbTab].totalPoints ||
        b[lbTab].exactCount - a[lbTab].exactCount ||
        b[lbTab].correctCount - a[lbTab].correctCount
      )
      .map((e, i) => ({ ...e, rank: i + 1 }));
  }, [leaderboard.data, lbTab]);

  const isOwner = room.data?.ownerId === user?.id;

  const sendInvite = useMutation({
    mutationFn: () => roomsApi.inviteByEmail(id!, inviteEmail),
    onSuccess: () => {
      setInviteEmail('');
      queryClient.invalidateQueries({ queryKey: ['room', id, 'members'] });
    },
  });

  const generateLink = useMutation({
    mutationFn: () => roomsApi.generateLink(id!),
    onSuccess: (data) => setGeneratedLink(data.url),
  });

  const inviteUser = useMutation({
    mutationFn: (userId: string) => roomsApi.createInvite(id!, userId),
    onSuccess: () => {
      setInviteAction(null);
      queryClient.invalidateQueries({ queryKey: ['room', id, 'invites'] });
    },
  });

  const cancelInvite = useMutation({
    mutationFn: (inviteId: string) => roomsApi.cancelInvite(id!, inviteId),
    onSuccess: () => {
      setInviteToCancel(null);
      queryClient.invalidateQueries({ queryKey: ['room', id, 'invites'] });
    },
  });

  const handleUserPick = (picked: UserSummary) => {
    if ((members.data ?? []).some((m) => m.userId === picked.id)) {
      setInviteAction({ kind: 'already-member', user: picked });
      return;
    }
    if ((pendingInvites.data ?? []).some((inv) => inv.inviteeUserId === picked.id)) {
      setInviteAction({ kind: 'already-invited', user: picked });
      return;
    }
    setInviteAction({ kind: 'confirm', user: picked });
  };

  const leave = useMutation({
    mutationFn: () => roomsApi.leave(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-rooms'] });
      setConfirmLeave(false);
      navigate('/rooms');
    },
  });

  const onCopyLink = async () => {
    if (!generatedLink) return;
    await navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareTitle = room.data ? `Dołącz do pokoju ${room.data.name}` : 'Zaproszenie do pokoju';
  const shareText = room.data
    ? `Zapraszam Cię do typowania w pokoju ${room.data.name} na WCTyper.`
    : 'Zapraszam Cię do typowania na WCTyper.';

  const onShare = async () => {
    if (!generatedLink) return;
    // pointer:coarse = touch primary (Android, iOS) — używamy natywnego share sheetu.
    // Desktop ma pointer:fine i tam pokazujemy popover, nawet gdy `navigator.share`
    // jest dostępne (Edge/Chrome desktop), bo wtedy zwykle nie ma sensownego targeta.
    const isTouchPrimary =
      typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
    if (isTouchPrimary && typeof navigator?.share === 'function') {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: generatedLink });
        return;
      } catch (err) {
        if ((err as DOMException)?.name === 'AbortError') return;
      }
    }
    setShareMenuOpen((open) => !open);
  };

  if (room.isLoading) return <DesktopShell><TopBar title="Ładowanie…" /></DesktopShell>;
  if (!room.data) return <DesktopShell><TopBar title="Nie znaleziono pokoju" /></DesktopShell>;

  return (
    <DesktopShell>
      <TopBar
        title={`${room.data.emoji ?? '🏆'} ${room.data.name}`}
        subtitle={`${members.data?.length ?? 0} członków · ${room.data.type === 'PRIVATE' ? 'prywatny' : 'publiczny'}`}
      />
      <div
        className="responsive-pad"
        style={{
          padding: '20px 36px 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <Link to="/rooms" style={{ textDecoration: 'none' }}>
          <Btn variant="ghost" size="sm" icon={<Ic.chevL size={14} />}>
            Wróć do pokojów
          </Btn>
        </Link>
        {isOwner ? (
          <Link to={`/rooms/${id}/manage`} style={{ textDecoration: 'none' }}>
            <Btn variant="ghost" size="sm" icon={<Ic.edit size={14} />}>
              Zarządzaj
            </Btn>
          </Link>
        ) : (
          <Btn
            variant="ghost"
            size="sm"
            icon={<Ic.logout size={14} />}
            onClick={() => setConfirmLeave(true)}
          >
            Opuść pokój
          </Btn>
        )}
      </div>
      <div
        className="stack-on-mobile responsive-pad"
        style={{
          flex: 1,
          padding: '16px 36px 28px',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
          gap: 20,
        }}
      >
        <Card padding={0}>
          <div style={{ padding: '20px 24px 0' }}>
            <h3 className="serif" style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
              Leaderboard
            </h3>
            <div style={{ fontSize: 12, color: '#5b6578', marginTop: 3 }}>Aktualne punkty członków pokoju</div>
          </div>
          <div
            ref={setTabsRowEl}
            className="leaderboard-tabs"
            style={{ padding: '12px 24px 10px', display: 'flex', justifyContent: 'center' }}
          >
            <Seg
              options={leaderboardTabOptions}
              value={lbTab}
              onChange={setLbTab}
              size="sm"
            />
          </div>

          {rankedLeaderboard.length > 0 ? (
            <div style={{ padding: '0 4px 12px' }}>
              <div
                className="leaderboard-row"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '40px minmax(0, 1fr) 80px 80px 80px 60px',
                  padding: '0 20px',
                  fontSize: 10.5,
                  color: '#5b6578',
                  fontWeight: 600,
                  letterSpacing: '.06em',
                  marginBottom: 6,
                }}
              >
                <div>#</div>
                <div>GRACZ</div>
                <div style={{ textAlign: 'right' }}>EXACT</div>
                <div style={{ textAlign: 'right' }}>HITS</div>
                <div style={{ textAlign: 'right' }}>BETS</div>
                <div style={{ textAlign: 'right' }}>PKT</div>
              </div>
              {rankedLeaderboard.map((e) => {
                const isMe = e.userId === user?.id;
                const stats = e[lbTab];
                return (
                  <div
                    key={e.userId}
                    className="leaderboard-row"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '40px minmax(0, 1fr) 80px 80px 80px 60px',
                      alignItems: 'center',
                      padding: '11px 20px',
                      margin: '0 4px',
                      borderRadius: 10,
                      background: isMe ? 'rgba(244,185,58,.08)' : 'transparent',
                      borderLeft: isMe ? '3px solid var(--accent)' : '3px solid transparent',
                    }}
                  >
                    <div className="num" style={{ color: '#5b6578', fontWeight: 600 }}>
                      {e.rank}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <Avatar nick={e.nick} size={30} />
                      <div className="leaderboard-name" style={{ minWidth: 0, overflow: 'hidden' }}>
                        <div style={{ fontWeight: 600, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {displayNick(e.nick)}
                          {isMe && <span style={{ color: '#5b6578', fontWeight: 500 }}> · Ty</span>}
                        </div>
                      </div>
                    </div>
                    <div className="num" style={{ textAlign: 'right', fontSize: 13, color: '#0f6b34' }}>
                      {stats.exactCount}
                    </div>
                    <div className="num" style={{ textAlign: 'right', fontSize: 13, color: '#5b6578' }}>
                      {stats.exactCount + stats.correctCount}
                    </div>
                    <div className="num" style={{ textAlign: 'right', fontSize: 13, color: '#5b6578' }}>
                      {stats.totalBets}
                    </div>
                    <div className="num serif" style={{ textAlign: 'right', fontSize: 16, fontWeight: 700 }}>
                      {stats.totalPoints}
                    </div>
                  </div>
                );
              })}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '4px 16px',
                  padding: '14px 20px 4px',
                  marginTop: 6,
                  borderTop: '1px solid rgba(0,0,0,.06)',
                  fontSize: 11.5,
                  color: '#5b6578',
                  lineHeight: 1.5,
                }}
              >
                <span><strong>EXACT</strong> — trafiony dokładny wynik (3 pkt)</span>
                <span><strong>HITS</strong> — wszystkie trafienia (dokładny wynik + rezultat)</span>
                <span><strong>BETS</strong> — wszystkie postawione typy</span>
              </div>
            </div>
          ) : (
            <div style={{ padding: 24, color: '#5b6578', textAlign: 'center' }}>
              Brak typów. Pierwsze punkty po wynikach meczów.
            </div>
          )}
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card padding={18}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h4 className="serif" style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                Członkowie
              </h4>
              <Pill color="gray">{members.data?.length ?? 0}</Pill>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {(members.data ?? []).slice(0, 12).map((m) => (
                <div key={m.userId} title={m.nick}>
                  <Avatar nick={m.nick ?? '?'} size={32} />
                </div>
              ))}
              {(members.data?.length ?? 0) > 12 && (
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: '#f4f3ee',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    color: '#5b6578',
                    fontWeight: 600,
                  }}
                >
                  +{(members.data?.length ?? 0) - 12}
                </div>
              )}
            </div>
          </Card>

          {(isOwner || room.data.membersCanInvite) && (
            <Card padding={18}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <h4 className="serif" style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                  Zaproś do pokoju
                </h4>
                <Ic.users size={14} style={{ color: 'var(--accent)' }} />
              </div>

              <div
                style={{
                  fontSize: 11,
                  color: '#5b6578',
                  fontWeight: 600,
                  letterSpacing: '.06em',
                  marginBottom: 6,
                }}
              >
                UŻYTKOWNIK Z APLIKACJI
              </div>
              <UserPicker
                onPick={handleUserPick}
                disabled={inviteUser.isPending}
              />

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  margin: '14px 0 12px',
                  color: '#8a94a6',
                  fontSize: 10.5,
                  fontWeight: 600,
                  letterSpacing: '.08em',
                }}
              >
                <div style={{ flex: 1, height: 1, background: 'rgba(15,30,61,.08)' }} />
                LUB E-MAILEM
                <div style={{ flex: 1, height: 1, background: 'rgba(15,30,61,.08)' }} />
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: '#5b6578',
                  fontWeight: 600,
                  letterSpacing: '.06em',
                  marginBottom: 6,
                }}
              >
                ZAPROSZENIE E-MAILEM
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <Ic.mail
                    size={13}
                    style={{
                      position: 'absolute',
                      left: 10,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#5b6578',
                    }}
                  />
                  <input
                    placeholder="rywal@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    style={{
                      width: '100%',
                      height: 36,
                      borderRadius: 8,
                      border: '1px solid rgba(15,30,61,.12)',
                      padding: '0 10px 0 28px',
                      fontSize: 12.5,
                      outline: 'none',
                      background: '#fff',
                    }}
                  />
                </div>
                <Btn
                  variant="primary"
                  size="sm"
                  style={{ height: 36 }}
                  onClick={() => sendInvite.mutate()}
                  disabled={!inviteEmail || sendInvite.isPending}
                >
                  Wyślij
                </Btn>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  margin: '2px 0 10px',
                  color: '#8a94a6',
                  fontSize: 10.5,
                  fontWeight: 600,
                  letterSpacing: '.08em',
                }}
              >
                <div style={{ flex: 1, height: 1, background: 'rgba(15,30,61,.08)' }} />
                LUB
                <div style={{ flex: 1, height: 1, background: 'rgba(15,30,61,.08)' }} />
              </div>
              <Btn
                variant="ghost"
                size="md"
                fullWidth
                style={{ boxShadow: 'inset 0 0 0 1.5px var(--accent)', marginBottom: 8 }}
                icon={<Ic.link size={13} />}
                onClick={() => generateLink.mutate()}
                disabled={generateLink.isPending}
              >
                {generateLink.isPending ? 'Generowanie…' : 'Wygeneruj link'}
              </Btn>
              {generatedLink && (
                <>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <div
                      className="mono"
                      style={{
                        flex: 1,
                        height: 34,
                        borderRadius: 8,
                        background: '#f7f6f2',
                        border: '1px solid rgba(15,30,61,.08)',
                        padding: '0 10px',
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: 11,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {generatedLink}
                    </div>
                    <button
                      onClick={onCopyLink}
                      aria-label="Kopiuj link"
                      title="Kopiuj link"
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 8,
                        border: '1px solid rgba(15,30,61,.1)',
                        background: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {copied ? <Ic.check size={13} /> : <Ic.copy size={13} />}
                    </button>
                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={onShare}
                        aria-label="Udostępnij link"
                        title="Udostępnij link"
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 8,
                          border: '1px solid rgba(15,30,61,.1)',
                          background: '#fff',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Ic.share size={13} />
                      </button>
                      {shareMenuOpen && (
                        <ShareMenu
                          url={generatedLink}
                          title={shareTitle}
                          text={shareText}
                          onClose={() => setShareMenuOpen(false)}
                        />
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: '#5b6578', marginTop: 8, textAlign: 'center' }}>
                    Link wygasa za 24 godziny.
                  </div>
                </>
              )}
            </Card>
          )}

          {(isOwner || room.data.membersCanInvite) && (pendingInvites.data ?? []).length > 0 && (
            <Card padding={18}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h4 className="serif" style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                  Oczekujące zaproszenia
                </h4>
                <Pill color="gray">{pendingInvites.data?.length}</Pill>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {pendingInvites.data?.map((inv) => {
                  const canCancel = isOwner || inv.inviterUserId === user?.id;
                  const days = Math.max(0, Math.round((new Date(inv.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
                  return (
                    <div
                      key={inv.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 12px',
                        background: '#f7f6f2',
                        borderRadius: 10,
                      }}
                    >
                      <Avatar nick={inv.inviteeNick ?? '?'} size={28} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {inv.inviteeNick ?? '—'}
                        </div>
                        <div style={{ fontSize: 11, color: '#5b6578' }}>
                          {inv.inviterUserId === user?.id ? 'wysłałeś Ty' : `wysłał ${inv.inviterNick ?? ''}`} · wygasa za {days} {days === 1 ? 'dzień' : 'dni'}
                        </div>
                      </div>
                      {canCancel && (
                        <button
                          onClick={() => setInviteToCancel(inv)}
                          aria-label="Anuluj zaproszenie"
                          style={{
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            padding: 6,
                            color: '#8a2a22',
                            display: 'flex',
                          }}
                        >
                          <Ic.trash size={14} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          <Card padding={18}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h4 className="serif" style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                Zasady punktacji
              </h4>
              <Ic.bolt size={14} style={{ color: 'var(--accent)' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12.5 }}>
              <div style={{ fontSize: 10, color: '#5b6578', fontWeight: 600, letterSpacing: '.06em' }}>
                WYNIK MECZU
              </div>
              <Rule pts={3} bg="rgba(31,157,85,.12)" fg="#0f6b34" label="Dokładny wynik" />
              <Rule pts={1} bg="rgba(244,185,58,.18)" fg="#6b4a00" label="Prawidłowy rezultat" />
              <Rule pts={0} bg="rgba(15,30,61,.06)" fg="#5b6578" label="Nie trafiony" />

              <div style={{ marginTop: 6, paddingTop: 12, borderTop: '1px solid rgba(15,30,61,.06)' }}>
                <div style={{ fontSize: 10, color: '#6b4a00', fontWeight: 600, letterSpacing: '.06em', marginBottom: 8 }}>
                  KARNE (FAZA PUCHAROWA, GDY MECZ KOŃCZY SIĘ REMISEM)
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Rule pts="+2" bg="rgba(31,157,85,.12)" fg="#0f6b34" label="Dokładny wynik serii karnych" />
                  <Rule pts="+1" bg="rgba(244,185,58,.18)" fg="#6b4a00" label="Trafiony zwycięzca serii karnych" />
                </div>
              </div>

              <div style={{ fontSize: 11.5, color: '#5b6578', marginTop: 6, lineHeight: 1.45 }}>
                W fazie grupowej liczy się wynik w podstawowym czasie gry (90' + doliczony). W fazie pucharowej
                liczymy wynik ostateczny — łącznie z dogrywką (120'); remis rozstrzygają karne, za które przyznajemy
                punkty bonusowe.
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Modal
        open={inviteAction?.kind === 'confirm'}
        onClose={() => !inviteUser.isPending && setInviteAction(null)}
        title="Zaprosić użytkownika do pokoju?"
        footer={
          inviteAction?.kind === 'confirm' ? (
            <>
              <Btn variant="ghost" size="md" onClick={() => setInviteAction(null)} disabled={inviteUser.isPending}>
                Anuluj
              </Btn>
              <Btn
                variant="accent"
                size="md"
                icon={<Ic.check size={14} />}
                onClick={() => inviteAction.kind === 'confirm' && inviteUser.mutate(inviteAction.user.id)}
                disabled={inviteUser.isPending}
              >
                {inviteUser.isPending ? 'Wysyłanie…' : 'Zaproś'}
              </Btn>
            </>
          ) : null
        }
      >
        {inviteAction?.kind === 'confirm' && (
          <>
            <p style={{ margin: '0 0 8px' }}>
              Wysłać zaproszenie do pokoju <strong>{room.data.name}</strong> dla użytkownika <strong>{inviteAction.user.nick}</strong>?
            </p>
            <p style={{ margin: 0, color: '#5b6578', fontSize: 13 }}>
              Użytkownik zobaczy zaproszenie w aplikacji i zdecyduje, czy je zaakceptować.
            </p>
            {inviteUser.isError && (
              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  background: 'rgba(208,70,60,.08)',
                  color: '#8a2a22',
                  borderRadius: 10,
                  fontSize: 13,
                }}
              >
                {(inviteUser.error as HttpError).body?.message ?? 'Nie udało się wysłać zaproszenia.'}
              </div>
            )}
          </>
        )}
      </Modal>

      <Modal
        open={inviteAction?.kind === 'already-member' || inviteAction?.kind === 'already-invited'}
        onClose={() => setInviteAction(null)}
        title={inviteAction?.kind === 'already-member' ? 'Użytkownik już należy' : 'Zaproszenie już wysłane'}
        footer={
          <Btn variant="primary" size="md" onClick={() => setInviteAction(null)}>
            OK
          </Btn>
        }
      >
        {inviteAction?.kind === 'already-member' && (
          <p style={{ margin: 0 }}>
            Użytkownik <strong>{inviteAction.user.nick}</strong> już należy do pokoju <strong>{room.data.name}</strong>.
          </p>
        )}
        {inviteAction?.kind === 'already-invited' && (
          <p style={{ margin: 0 }}>
            Użytkownik <strong>{inviteAction.user.nick}</strong> ma już oczekujące zaproszenie do pokoju <strong>{room.data.name}</strong>.
          </p>
        )}
      </Modal>

      <Modal
        open={!!inviteToCancel}
        onClose={() => !cancelInvite.isPending && setInviteToCancel(null)}
        title="Anulować zaproszenie?"
        footer={
          <>
            <Btn variant="ghost" size="md" onClick={() => setInviteToCancel(null)} disabled={cancelInvite.isPending}>
              Wróć
            </Btn>
            <Btn
              variant="danger"
              size="md"
              icon={<Ic.trash size={14} />}
              onClick={() => inviteToCancel && cancelInvite.mutate(inviteToCancel.id)}
              disabled={cancelInvite.isPending}
            >
              {cancelInvite.isPending ? 'Anulowanie…' : 'Anuluj zaproszenie'}
            </Btn>
          </>
        }
      >
        {inviteToCancel && (
          <p style={{ margin: 0 }}>
            Cofnąć zaproszenie dla użytkownika <strong>{inviteToCancel.inviteeNick ?? '—'}</strong>? Użytkownik nie zobaczy go już w swojej skrzynce.
          </p>
        )}
      </Modal>

      <Modal
        open={confirmLeave}
        onClose={() => !leave.isPending && setConfirmLeave(false)}
        title="Opuścić pokój?"
        footer={
          <>
            <Btn variant="ghost" size="md" onClick={() => setConfirmLeave(false)} disabled={leave.isPending}>
              Anuluj
            </Btn>
            <Btn
              variant="danger"
              size="md"
              icon={<Ic.logout size={14} />}
              onClick={() => leave.mutate()}
              disabled={leave.isPending}
            >
              {leave.isPending ? 'Opuszczanie…' : 'Opuść pokój'}
            </Btn>
          </>
        }
      >
        <p style={{ margin: '0 0 8px' }}>
          Pokój <strong>{room.data?.name}</strong> przestanie się pojawiać na Twojej liście pokojów.
        </p>
        <p style={{ margin: 0, color: '#5b6578', fontSize: 13 }}>
          Twoje typy zostają w bazie, ale nie będziesz już liczony w leaderboardzie pokoju. Możesz dołączyć ponownie, jeśli ktoś Cię zaprosi lub jeśli pokój jest publiczny.
        </p>
        {leave.isError && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              background: 'rgba(208,70,60,.08)',
              color: '#8a2a22',
              borderRadius: 10,
              fontSize: 13,
            }}
          >
            {(leave.error as HttpError).body?.message ?? 'Nie udało się opuścić pokoju.'}
          </div>
        )}
      </Modal>
    </DesktopShell>
  );
}

function ShareMenu({
  url,
  title,
  text,
  onClose,
}: {
  url: string;
  title: string;
  text: string;
  onClose: () => void;
}) {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedTextWithUrl = encodeURIComponent(`${text} ${url}`);
  const encodedBody = encodeURIComponent(`${text}\n\n${url}`);

  const items: { label: string; href: string }[] = [
    { label: 'WhatsApp', href: `https://wa.me/?text=${encodedTextWithUrl}` },
    { label: 'Telegram', href: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}` },
    { label: 'E-mail', href: `mailto:?subject=${encodedTitle}&body=${encodedBody}` },
  ];

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 40,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          right: 0,
          minWidth: 160,
          background: '#fff',
          border: '1px solid rgba(15,30,61,.1)',
          borderRadius: 10,
          boxShadow: '0 6px 20px rgba(15,30,61,.12)',
          overflow: 'hidden',
          zIndex: 50,
        }}
        role="menu"
      >
        {items.map((it) => (
          <a
            key={it.label}
            href={it.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            role="menuitem"
            style={{
              display: 'block',
              padding: '10px 14px',
              fontSize: 13,
              color: '#0f1e3d',
              textDecoration: 'none',
              borderBottom: '1px solid rgba(15,30,61,.06)',
            }}
          >
            {it.label}
          </a>
        ))}
      </div>
    </>
  );
}

function Rule({ pts, bg, fg, label }: { pts: number | string; bg: string; fg: string; label: string }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
      <div
        className="mono"
        style={{
          width: 26,
          height: 26,
          borderRadius: 7,
          background: bg,
          color: fg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
        }}
      >
        {pts}
      </div>
      <div>{label}</div>
    </div>
  );
}

function UserPicker({ onPick, disabled }: { onPick: (user: UserSummary) => void; disabled?: boolean }) {
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 250);
  const results = useQuery({
    queryKey: ['users', 'search', debounced],
    queryFn: () => roomsApi.searchUsers(debounced),
    enabled: debounced.trim().length >= 2,
  });
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <Ic.users
          size={13}
          style={{
            position: 'absolute',
            left: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#5b6578',
          }}
        />
        <input
          placeholder="wyszukaj po nicku…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          disabled={disabled}
          style={{
            width: '100%',
            height: 36,
            borderRadius: 8,
            border: '1px solid rgba(15,30,61,.12)',
            padding: '0 10px 0 28px',
            fontSize: 12.5,
            outline: 'none',
            background: '#fff',
          }}
        />
      </div>
      {open && debounced.trim().length >= 2 && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            background: '#fff',
            border: '1px solid rgba(15,30,61,.1)',
            borderRadius: 10,
            boxShadow: '0 6px 20px rgba(15,30,61,.12)',
            maxHeight: 220,
            overflowY: 'auto',
            zIndex: 50,
          }}
        >
          {(results.data ?? []).length === 0 ? (
            <div style={{ padding: 10, fontSize: 12, color: '#5b6578' }}>
              {results.isFetching ? 'Szukanie…' : 'Brak wyników.'}
            </div>
          ) : (
            (results.data ?? []).map((u) => (
              <button
                key={u.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onPick(u);
                  setQuery('');
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '8px 10px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: 13,
                }}
              >
                <Avatar nick={u.nick} size={22} />
                <span style={{ fontWeight: 500 }}>{u.nick}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}
